import { TrashIcon } from "lucide-react";
import {
  SetURLSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Button } from "src/components/buttons/shadcn-button";
import { CustomerListPagination } from "./customer-pagination";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Customer } from "src/redux/types/_customer.type";
import { useDebounce } from "use-debounce";
import Search from "src/components/input/Search";
import { getTitle, updateSearchParams1 } from "src/utils";
import { StateList } from "src/utils/states";
import * as XLSX from "xlsx";
import moment from "moment";
import { CustomerFilters, useFetchCustomers } from "../hooks/useFetchCustomers";
import { useDeleteCustomers } from "../hooks/useDeleteCustomers";
import { Badge } from "src/components/ui/Badge";
import AssignMultipleCustomer from "./AssignMultipleCustomer";

const setSearchParamsFilter = (
  updates: Record<string, string>,
  setSearchParams: SetURLSearchParams,
  resetPage = true,
) => {
  setSearchParams(
    (prev) => {
      const next = Object.fromEntries(prev);

      for (const [key, value] of Object.entries(updates)) {
        if (value.trim() === "") {
          delete next[key];
        } else {
          next[key] = value;
        }
      }

      if (resetPage) next.page = "1";

      return next;
    },
    { replace: true },
  );
};

const CustomerList = () => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [failSafePageNumber, setFailSafePageNumber] = useState<number>(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const state = searchParams.get("state") || "";
  const district = searchParams.get("district") || "";
  const email = searchParams.get("email") || "";
  const name = searchParams.get("name") || "";
  const employee_name = searchParams.get("employee_name") || "";
  const employee_code = searchParams.get("employee_code") || "";
  const fromDate = searchParams.get("fromDate") || "";
  const toDate = searchParams.get("toDate") || "";
  const health_seeker = searchParams.get("health_seeker") || "";
  const pagesize = parseInt(searchParams.get("pagesize") || "25", 10);
  const pageNumber = parseInt(searchParams.get("page") || "1", 10);

  const [debouncedName] = useDebounce(name, 800);
  const [debouncedEmail] = useDebounce(email, 800);
  const [debouncedEmployeeName] = useDebounce(employee_name, 1000);
  const [debouncedEmployeeCode] = useDebounce(employee_code, 1000);

  const isHealthSeeker = health_seeker === "true";

  const selectedState = StateList.find((item) => item.state === state);
  const districts = selectedState ? selectedState.districts : [];

  const filters: CustomerFilters = {
    state,
    district,
    fromDate,
    toDate,
    health_seeker,
    page: pageNumber,
    pagesize,
    name: debouncedName,
    email: debouncedEmail,
    employee_name: debouncedEmployeeName,
    employee_code: debouncedEmployeeCode,
  };

  const { data, isLoading, isFetching, isError, error } =
    useFetchCustomers(filters);
  const customers = data ?? [];

  const hasNextPage = customers.length === pagesize;
  const hasPrevPage = pageNumber > 1;

  useEffect(() => {
    if (!customers) return;
    if (customers?.length > 0) {
      setFailSafePageNumber(pageNumber);
    } else if (failSafePageNumber !== pageNumber) {
      toast.info("No more customers!");
      setSearchParams({
        ...Object.fromEntries(searchParams),
        page: failSafePageNumber.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isError) {
      toast.error("Failed to load customers");
      console.error(error, "Failed to load customer");
    }
  }, [isError, error]);

  const handleNextPage = () => {
    if (!hasNextPage) {
      toast.info("No more customers!");
      return;
    }
    setFailSafePageNumber(pageNumber);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (pageNumber + 1).toString(),
    });
  };

  const handlePrevPage = () => {
    if (!hasPrevPage) {
      toast.info("This is first page");
      return;
    }
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (pageNumber - 1).toString(),
    });
  };

  const handleJumpToPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });
  };

  function clearAllFilters(): void {
    setSearchParams({});
  }

  // toggle select all customers
  const toggleCustomer = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  // handle select all customers
  const handleSelectAll = () => {
    if (selectedIds.length === customers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(customers.map((c) => c.id));
    }
  };

  // copy emails
  const onCopyEmails = async () => {
    const values = selectedIds.values();
    if (values) {
      const customerList = Array.from(values);
      let emailText = "";
      customerList.forEach((customer) => {
        const result = customers.find((item) => item.id === customer);
        if (result) {
          if (emailText.length > 0) {
            emailText = `${emailText},${result.email}`;
          } else {
            emailText = result.email;
          }
        }
      });
      if (emailText.length > 0) {
        await navigator.clipboard.writeText(emailText);
        toast(`Successfully Copied to Clipboard!`, {
          type: "success",
        });
      } else {
        toast(`Please select items to Copy to Clipboard!`, {
          type: "error",
        });
      }
    }
  };

  // delete selected customers
  const { mutate: deleteSelected, isPending } = useDeleteCustomers();

  const deleteSelectedCustomers = async () => {
    if (selectedIds.length === 0) {
      toast.info("Select Customers to Delete!");
      return;
    }

    const selectedCustomers = customers.filter((customer) =>
      selectedIds.includes(customer.id),
    );

    const customerIds = selectedCustomers.map((c) => c.id);
    const customerEmails = selectedCustomers.map((c) => c.email);

    deleteSelected({
      ids: customerIds,
      emails: customerEmails,
    });
  };

  // export customers list to excel
  const createCustomerExcel = (
    customers: Customer[],
    fileName: string = "Customer-list",
  ) => {
    try {
      const data = customers.map((customer) => ({
        Name: `${customer.first_name?.toUpperCase()} ${customer.middle_name?.toUpperCase()} ${customer.last_name?.toUpperCase()}`,
        "Assigned Employee Name": customer.assigned_employee
          ? customer.assigned_employee.employee_name
          : "Not Assigned",
        "Assigned Employee Code": customer.assigned_employee
          ? customer.assigned_employee.employee_code
          : "--",
        "Phone Number": customer.phone_no,
        "Whatsapp Number": customer.whatsapp_number ?? customer.phone_no,
        Email: customer.email,
        Gender: customer.gender,
        "Subscription Type": customer.subscription
          ? getTitle(
              customer.subscription.duration,
              customer.subscription.member,
            )
          : "NONE",
        "Subscribed On": customer.subscription
          ? moment(customer.subscription.created_at).format("MMM Do YY")
          : "--",
        "Registered On": moment(customer.created_at).format("MMM Do YY"),
        State: customer.state?.toUpperCase(),
        District: customer.district?.toUpperCase(),
        "City/Town": customer.city?.toUpperCase(),
        Address: customer.address,
        "Pin Code": customer.pin,
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);

      const columnWidths = [
        { wch: 25 }, // Name
        { wch: 25 }, // Assigned Employee Name
        { wch: 25 }, // Assigned Employee Code
        { wch: 20 }, // Phone Number
        { wch: 20 }, // Whatsapp Number
        { wch: 25 }, // Email
        { wch: 25 }, // Gender
        { wch: 20 }, // Type
        { wch: 20 }, // Subscribed On
        { wch: 20 }, // Registered On
        { wch: 20 }, // State
        { wch: 20 }, // District
        { wch: 20 }, // City
        { wch: 20 }, // Address
        { wch: 15 }, // Pin Code
      ];
      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");

      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    } catch (error) {
      console.error("Error exporting Customers to Excel:", error);
      throw new Error("Failed to export Customers to Excel");
    }
  };

  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    if(selectedIds.length > 0) {
      setIsOpen(!isOpen);
    } else {
      toast.info("Select customers to assign");
    }
  };

  const handleClose = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div>
      <div className="rounded-xl border border-slate-200 bg-white/40 backdrop-blur-md sticky top-0 p-4 shadow-sm z-50 mb-3">
        <div className="grid grid-cols-6 gap-2">
          <div className="flex flex-col gap-2">
            <Search
              className="h-9 w-full text-sm"
              value={name}
              placeholder="Search by customer name..."
              onChange={(value) =>
                updateSearchParams1("name", value, setSearchParams)
              }
            />
            <Search
              className="h-9 w-full text-sm"
              value={email}
              placeholder="Search by customer email..."
              onChange={(value) =>
                updateSearchParams1("email", value, setSearchParams)
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <div>
              <select
                className="select select-bordered h-9 min-h-9 w-full rounded-lg border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
                value={state}
                onChange={(e) => {
                  const newState = e.target.value;

                  setSearchParamsFilter(
                    { state: newState, district: "" },
                    setSearchParams,
                  );
                }}
              >
                <option value="">All states</option>

                {StateList.map((item) => (
                  <option key={item.state} value={item.state}>
                    {item.state}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                className="select select-bordered h-9 min-h-9 w-full rounded-lg border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                value={district}
                onChange={(e) =>
                  setSearchParamsFilter(
                    { district: e.target.value },
                    setSearchParams,
                  )
                }
                disabled={!state}
              >
                <option value="">
                  {state ? "All districts" : "Select state first"}
                </option>

                {districts.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-start">
            <label
              className={`flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg border px-3 transition
              ${isHealthSeeker ? "border-primary/30 bg-primary/5" : "border-slate-200 bg-white hover:bg-slate-50"}
              `}
            >
              <input
                type="checkbox"
                className="checkbox checkbox-sm rounded-md"
                checked={isHealthSeeker}
                onChange={() => {
                  setSearchParamsFilter(
                    { health_seeker: !isHealthSeeker ? "true" : "" },
                    setSearchParams,
                  );
                }}
              />

              <span
                className={`text-sm font-medium ${
                  isHealthSeeker ? "text-primary" : "text-slate-600"
                }`}
              >
                Subscribers
              </span>
            </label>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">
              From date
            </label>

            <input
              type="date"
              className="input input-bordered h-9 w-full rounded-lg border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
              value={fromDate}
              onChange={(e) =>
                setSearchParamsFilter(
                  { fromDate: e.target.value },
                  setSearchParams,
                )
              }
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">
              To date
            </label>

            <input
              type="date"
              className="input input-bordered h-9 w-full rounded-lg border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
              value={toDate}
              onChange={(e) =>
                setSearchParamsFilter(
                  { toDate: e.target.value },
                  setSearchParams,
                )
              }
            />
          </div>
          <div className="flex flex-col gap-2">
            <Search
              className="h-9 w-full text-sm"
              value={employee_name}
              placeholder="Search by Employee Name"
              onChange={(value) =>
                updateSearchParams1("employee_name", value, setSearchParams)
              }
            />
            <Search
              className="h-9 w-full text-sm"
              value={employee_code}
              placeholder="Search by Employee Code"
              onChange={(value) =>
                updateSearchParams1(
                  "employee_code",
                  value.replace(/\D/g, ""),
                  setSearchParams,
                )
              }
            />
          </div>
        </div>

        {(name ||
          email ||
          employee_name ||
          employee_code ||
          state ||
          district ||
          fromDate ||
          toDate ||
          isHealthSeeker) && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs font-medium text-slate-500">
              Active filters:
            </span>

            {name && (
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                Name: {name}
              </span>
            )}

            {email && (
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                Email: {email}
              </span>
            )}

            {employee_name && (
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                Employee Name: {employee_name}
              </span>
            )}

            {employee_code && (
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                Employee Code: {employee_code}
              </span>
            )}

            {state && (
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                {state}
              </span>
            )}

            {district && (
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                {district}
              </span>
            )}

            {fromDate && (
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                From: {fromDate}
              </span>
            )}

            {toDate && (
              <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                To: {toDate}
              </span>
            )}

            {isHealthSeeker && (
              <span className="rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                Subscriptions
              </span>
            )}
            <div className="flex items-end justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={clearAllFilters}
                className="h-8 w-fit gap-1.5 px-2.5 text-xs font-medium text-slate-500 hover:bg-red-50 hover:text-red-600"
              >
                <TrashIcon className="size-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mb-3">
        <div className="">
          {isLoading || isFetching ? (
            <div className="flex justify-center items-center h-full">
              <span className="loading loading-dots loading-lg"></span>
            </div>
          ) : (
            <div className="">
              <table className="table table-xs">
                {/* head */}
                <thead>
                  <tr>
                    <th className="text-md font-semibold">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={selectedIds.length === customers.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    {[
                      "Name",
                      "Assigned Employee",
                      "Contact Number",
                      "whatsapp Number",
                      "Subscription Type",
                      "Registered",
                      "Location",
                      "Actions",
                    ].map((header) => (
                      <th key={header}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(customers) &&
                    customers.map((customer) => (
                      <tr key={customer.id} className="hover ">
                        <td>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-sm"
                            checked={selectedIds.includes(customer.id)}
                            onChange={() => toggleCustomer(customer.id)}
                          />
                        </td>
                        <td>
                          <div>
                            <p className="font-medium">
                              {customer.first_name} {customer.last_name}
                            </p>
                            <p className="text-muted">{customer.email}</p>
                          </div>
                        </td>
                        <td>
                          {customer.assigned_employee ? (
                            <div>
                              <p className="text-sm">
                                {customer.assigned_employee.employee_name}
                              </p>
                              <p className="text-xs text-muted">
                                {customer.assigned_employee.employee_code}
                              </p>
                            </div>
                          ) : (
                            <p className="text-muted text-sm">Not Assigned</p>
                          )}
                        </td>
                        <td>{customer.phone_no}</td>
                        <td>
                          {customer.whatsapp_number
                            ? customer.whatsapp_number
                            : customer.phone_no}
                        </td>
                        <td>
                          <Badge
                            className={`py-1 px-2 ${
                              customer.health_seeker && customer.subscription
                                ? "bg-primary"
                                : "bg-gray-200"
                            }`}
                          >
                            {customer.health_seeker && customer.subscription
                              ? getTitle(
                                  customer.subscription.duration,
                                  customer.subscription.member,
                                )
                              : "None"}
                          </Badge>
                          <p className="text-xs text-gray-400 mt-1">
                            {customer.subscription &&
                              moment(customer.subscription.created_at).format(
                                "MMM Do YY",
                              )}
                          </p>
                        </td>
                        <td>
                          {new Date(customer.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div>
                            <p className="font-medium">{customer.state}</p>
                            <p className="text-muted">{customer.district}</p>
                          </div>
                        </td>
                        <td>
                          <Button
                            variant="outline"
                            onClick={() =>
                              navigate("/admin/manage-customers/details", {
                                state: { customer },
                              })
                            }
                          >
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-between items-center p-3 border rounded-md rounded-t-none">
        <div>
          {/* page size*/}
          <div className="flex items-center justify-between px-3 py-2 border rounded-md border-primary">
            <label className="font-semibold">Page Size</label>
            <select
              value={pagesize}
              onChange={(e) => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  pagesize: e.target.value,
                });
              }}
              className="border p-1 rounded-md ml-2"
            >
              {[10, 25, 50].map((value) => (
                <option value={value}>{value}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="">
          <CustomerListPagination
            currentPageNumber={pageNumber}
            onNext={handleNextPage}
            onPrev={handlePrevPage}
            onJumpToPage={handleJumpToPage}
            isFetching={isFetching}
            hasPrev={hasPrevPage}
            hasNext={hasNextPage}
          />
        </div>
        <div className="join">
          <button
            className="btn btn-xs btn-outline sm:btn-sm md:btn-md join-item"
            onClick={handleOpen}
          >
            Assign
          </button>
          <button
            className="btn btn-xs btn-outline sm:btn-sm md:btn-md join-item"
            onClick={() => createCustomerExcel(customers)}
          >
            Get in Excel
          </button>
          <button
            className="btn btn-xs btn-outline sm:btn-sm md:btn-md join-item"
            onClick={onCopyEmails}
          >
            Copy Emails
          </button>
          <button
            className="btn btn-xs btn-outline sm:btn-sm md:btn-md join-item"
            onClick={deleteSelectedCustomers}
            disabled={isPending}
          >
            {isPending ? (
              <span className="loading loading-dots loading-md"></span>
            ) : (
              "Delete Selected"
            )}
          </button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <AssignMultipleCustomer
          customerIds={selectedIds}
          isOpen={isOpen}
          close={handleClose}
        />
      )}
    </div>
  );
};

export default CustomerList;
